/** Small display helpers for the console (all API timestamps are ISO-8601 UTC, docs/01 §6). */

/** `08/09 09:12` — the compact stamp of the audit table (Figma CMS-14). */
export function formatStamp(iso: string, locale = 'es'): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * `formatStamp` to the second.
 *
 * QA 2026-09-15 **UX-575**: /audit's row name is `action · entity · time`, and bulk operations
 * share all three to the minute — three «grant.create · grant import:0 · 17/9, 02:56» in a row.
 * The seconds are what separates them.
 */
export function formatStampSeconds(iso: string, locale = 'es'): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatDateTime(iso: string | null, locale = 'es'): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/**
 * Thousands separators for the dashboard counters. `useGrouping: 'always'` because Spanish
 * defaults to `min2` (no separator below 10 000) and Figma CMS-02 shows "1.842".
 */
const GROUPING = { useGrouping: 'always' } as unknown as Intl.NumberFormatOptions;

export function formatNumber(value: number, locale = 'es'): string {
  return new Intl.NumberFormat(locale, GROUPING).format(value);
}

/** Pretty JSON for the diff viewer; primitives are rendered as-is. */
export function stringifyValue(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') return JSON.stringify(value);
  return JSON.stringify(value);
}

/**
 * `31 de diciembre de 2026` from the `YYYY-MM-DD` an `<input type="date">` holds.
 *
 * QA iteration 2 UX-210: the widget itself paints `mm/dd/yyyy` or `dd/mm/yyyy` from the
 * *browser's* locale, not the console's, so an operator in Lima reading a Spanish CMS can set
 * the wrong expiry. The console echoes the chosen day back in words, where no order is implied.
 * Parsed as UTC noon so a negative offset cannot walk the day backwards.
 */
export function formatDateLong(value: string, locale = 'es'): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'UTC' }).format(date);
}
